import { reflect } from '@effector/reflect';
import { Select, TextInput } from '@mantine/core';
import { combine } from 'effector';
import { useUnit } from 'effector-react';
import { Plus } from 'lucide-react';
import { not } from 'patronum';
import { useTranslation } from 'react-i18next';
import {
	CASHDESK_NAME_MAX_LENGTH,
	CASHDESK_NAME_MIN_LENGTH,
	CASHDESK_NOTE_MAX_LENGTH,
} from 'entities/cashdesk';
import { viewerModel } from 'entities/viewer';
import { $t, translated } from 'shared/lib';
import { createModalForm, MultiSelect } from 'shared/ui';
import { AddCashdeskModel } from '../../model';
import cls from './add-cashdesk-modal.module.scss';

export const createAddCashdeskModal = (model: AddCashdeskModel) => {
	const ModalForm = createModalForm(model);

	const NameField = reflect({
		view: TextInput,
		bind: {
			'data-test-id': 'add-cashdesk-name-input',
			value: model.form.fields.name.$value,
			onChange: (e) => model.form.fields.name.change(e.target.value),
			label: translated('add-cashdesk.name'),
			placeholder: translated('add-cashdesk.name'),
			errorProps: { 'data-test-id': 'add-cashdesk-name-input-error' },
			error: combine(
				[model.form.fields.name.$errors, $t],
				([errors, t]) =>
					errors.length > 0 &&
					t(`errors:validation.${errors[0]}`, {
						fieldName: t('add-cashdesk.name'),
						maxLength: CASHDESK_NAME_MAX_LENGTH,
						minLength: CASHDESK_NAME_MIN_LENGTH,
					}),
			),
			required: true,
		},
	});

	const AccountField = reflect({
		view: Select,
		bind: {
			'data-test-id': 'add-cashdesk-account-select',
			value: model.form.fields.accountId.$value.map((value) => value?.toString() || null),
			onChange: (value: string | null) =>
				model.form.fields.accountId.change(value === null ? value : Number(value)),
			data: model.$accounts,
			disabled: model.form.fields.accountId.$disabled,
			label: translated('add-cashdesk.account'),
			placeholder: translated('add-cashdesk.account'),
			errorProps: { 'data-test-id': 'add-cashdesk-account-select-error' },
			error: combine(
				[model.form.fields.accountId.$errors, $t],
				([errors, t]) =>
					errors.length > 0 &&
					t(`errors:validation.${errors[0]}`, {
						fieldName: t('add-cashdesk.account'),
					}),
			),
			searchable: true,
			required: true,
		},
	});

	const CurrencyField = reflect({
		view: Select,
		bind: {
			'data-test-id': 'add-cashdesk-currency-select',
			value: model.form.fields.currency.$value,
			onChange: model.form.fields.currency.change,
			data: model.$currencies,
			disabled: model.form.fields.currency.$disabled,
			label: translated('add-cashdesk.currency'),
			placeholder: translated('add-cashdesk.currency'),
			errorProps: { 'data-test-id': 'add-cashdesk-currency-select-error' },
			error: combine(
				[model.form.fields.currency.$errors, $t],
				([errors, t]) =>
					errors.length > 0 &&
					t(`errors:validation.${errors[0]}`, {
						fieldName: t('add-cashdesk.currency'),
					}),
			),
			searchable: true,
			required: true,
		},
	});

	const CashiersField = reflect({
		view: MultiSelect,
		bind: {
			'data-test-id': 'add-cashdesk-cashiers-select',
			value: model.form.fields.cashiers.$value.map((cashiers) => cashiers.map(String)),
			onChange: (values) => model.form.fields.cashiers.change(values.map(Number)),
			data: model.$cashiers,
			disabled: model.form.fields.cashiers.$disabled,
			label: translated('add-cashdesk.cashiers'),
			placeholder: translated('add-cashdesk.cashiers'),
			nothingFoundMessage: translated('add-cashdesk.cashiers-not-found'),
			fullWidth: true,
		},
	});

	const NoteField = reflect({
		view: TextInput,
		bind: {
			'data-test-id': 'add-cashdesk-note-input',
			value: model.form.fields.note.$value,
			onChange: (e) => model.form.fields.note.change(e.target.value),
			label: translated('add-cashdesk.note'),
			placeholder: translated('add-cashdesk.note'),
			errorProps: { 'data-test-id': 'add-cashdesk-note-input-error' },
			error: combine(
				[model.form.fields.note.$errors, $t],
				([errors, t]) =>
					errors.length > 0 &&
					t(`errors:validation.${errors[0]}`, {
						fieldName: t('add-cashdesk.note'),
						maxLength: CASHDESK_NOTE_MAX_LENGTH,
					}),
			),
		},
	});

	const AddCashdeskModalButton = reflect({
		view: ModalForm.Button,
		bind: {
			'data-test-id': 'add-cashdesk-button',
			leftSection: <Plus />,
			className: cls.addCashdeskModalButton,
			disabled: not(viewerModel.$canWriteCashdesks),
			children: translated('add-cashdesk.label'),
			modalData: null,
		},
	});

	const AddCashdeskModal = () => {
		const { t } = useTranslation();

		const [pending, close] = useUnit([model.$pending, model.modalClosed]);

		return (
			<ModalForm
				title={t('add-cashdesk.title')}
				cancelButtonProps={{
					children: t('add-cashdesk.cancel'),
				}}
				submitButtonProps={{
					children: t('add-cashdesk.add'),
					loading: pending,
				}}
				onExitTransitionEnd={close}
			>
				<NameField />
				<AccountField />
				<CurrencyField />
				<CashiersField />
				<NoteField />
			</ModalForm>
		);
	};

	return Object.assign(AddCashdeskModal, {
		Button: AddCashdeskModalButton,
	});
};
